import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  Box,
} from '@mui/material';
import { alterarStatusReembolso } from '../../../services/pedidoReembolsoService';
import type { StatusReembolso } from '../../../types/pedidoReembolso';

export interface AlterarStatusReembolsoDialogProps {
  open: boolean;
  pedidoId: string;
  novoStatus: 'APROVADO' | 'RECUSADO';
  onClose: () => void;
  onSuccess: () => void;
}

const AlterarStatusReembolsoDialog: React.FC<AlterarStatusReembolsoDialogProps> = ({
  open,
  pedidoId,
  novoStatus,
  onClose,
  onSuccess,
}) => {
  const [motivoRecusa, setMotivoRecusa] = useState('');
  const [observacaoOrganizador, setObservacaoOrganizador] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const isRecusa = novoStatus === 'RECUSADO';
  const title = isRecusa ? 'Recusar Pedido de Reembolso' : 'Aprovar Pedido de Reembolso';

  const handleSubmit = async () => {
    if (isRecusa && !motivoRecusa.trim()) return;
    setSaving(true);
    try {
      await alterarStatusReembolso({
        pedidoId,
        novoStatus: novoStatus as StatusReembolso,
        motivoRecusa: isRecusa ? motivoRecusa.trim() : undefined,
        observacaoOrganizador: observacaoOrganizador.trim() || undefined,
      });
      setMotivoRecusa('');
      setObservacaoOrganizador('');
      onSuccess();
      onClose();
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Erro ao alterar status do reembolso.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleClose = () => {
    if (saving) return;
    setMotivoRecusa('');
    setObservacaoOrganizador('');
    onClose();
  };

  const submitDisabled = saving || (isRecusa && !motivoRecusa.trim());

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {isRecusa && (
              <TextField
                label="Motivo da recusa"
                value={motivoRecusa}
                onChange={(e) => setMotivoRecusa(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={3}
                required
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                placeholder="Informe o motivo da recusa"
              />
            )}
            <TextField
              label="Observacao do organizador (opcional)"
              value={observacaoOrganizador}
              onChange={(e) => setObservacaoOrganizador(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              disabled={saving}
              InputLabelProps={{ shrink: true }}
              placeholder="Observacao adicional"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={isRecusa ? 'error' : 'primary'}
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {saving ? 'Salvando...' : isRecusa ? 'Recusar' : 'Aprovar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AlterarStatusReembolsoDialog;
